import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  acceptClanInvite,
  createClan,
  declineClanInvite,
  listClans,
  listMyClanInvites,
} from "@/api/clans";
import { Alert, Button, Input, Spinner, cx } from "@/components/ui";
import { useTranslation } from "react-i18next";
import {
  CLASS_EMOJIS,
  CLASS_PALETTE,
  ClassIcon,
  classSwatch,
  shortRoleLabel,
} from "./class-theme";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/** Incoming class invitations — accept (joins the class) or decline. */
function IncomingInvites() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["my-clan-invites"], queryFn: listMyClanInvites });

  const acceptMut = useMutation({
    mutationFn: (inviteId: string) => acceptClanInvite(inviteId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["my-clan-invites"] });
      qc.invalidateQueries({ queryKey: ["clans"] });
      if (res.clan) navigate(`/app/community/clans/${res.clan.id}`);
    },
  });
  const declineMut = useMutation({
    mutationFn: (inviteId: string) => declineClanInvite(inviteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-clan-invites"] }),
  });

  const invites = data ?? [];
  if (invites.length === 0) return null;

  return (
    <div className="mb-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">
        {t("Class invitations")}
      </h2>
      <div className="mt-3 space-y-2">
        {invites.map((inv) => (
          <div key={inv.id} className="card flex items-center gap-3 p-3.5">
            <ClassIcon emoji={inv.clan_icon_emoji} color={inv.clan_color} size={40} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-ink">{inv.clan_name}</p>
              <p className="text-xs text-ink-faint">
                {t("Invited by {{name}}", { name: inv.inviter_display_name })}
                {inv.role !== "member" && ` · ${shortRoleLabel(inv.role)}`}
              </p>
            </div>
            <Button
              size="sm"
              loading={acceptMut.isPending && acceptMut.variables === inv.id}
              onClick={() => acceptMut.mutate(inv.id)}
            >
              {t("Accept")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              loading={declineMut.isPending && declineMut.variables === inv.id}
              onClick={() => declineMut.mutate(inv.id)}
            >
              {t("Decline")}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Paste a class code to jump to the join preview. */
function JoinByCodeBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const cleaned = code.trim().toUpperCase();
  return (
    <form
      className="mb-5 flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (cleaned) navigate(`/app/community/join/${encodeURIComponent(cleaned)}`);
      }}
    >
      <Input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder={t("Have a class code? Enter it here")}
        className="text-sm uppercase tracking-widest"
      />
      <Button size="sm" variant="secondary" type="submit" disabled={!cleaned}>
        {t("Join")}
      </Button>
    </form>
  );
}

export function ClanList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["clans"],
    queryFn: listClans,
  });

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [emoji, setEmoji] = useState("");
  const [color, setColor] = useState("");
  const [inviteOnly, setInviteOnly] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      createClan({
        name: name.trim(),
        slug: slug.trim() || slugify(name),
        description: description.trim() || null,
        icon_emoji: emoji || null,
        color: color || null,
        join_policy: inviteOnly ? "invite" : "open",
      }),
    onSuccess: (clan) => {
      qc.invalidateQueries({ queryKey: ["clans"] });
      navigate(`/app/community/clans/${clan.id}`);
    },
    onError: (e) => setFormError(e instanceof Error ? e.message : t("Couldn't create class.")),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const finalSlug = slug.trim() || slugify(name);
    if (name.trim().length < 1) return setFormError(t("Give your class a name."));
    if (!/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(finalSlug))
      return setFormError(t("Slug must be 3–64 lowercase letters, numbers, or hyphens."));
    createMut.mutate();
  };

  return (
    <div>
      <IncomingInvites />
      <JoinByCodeBar />
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t("Your classes")}</h2>
        <Button size="sm" variant={creating ? "ghost" : "primary"} onClick={() => setCreating((v) => !v)}>
          {creating ? t("Cancel") : t("New class")}
        </Button>
      </div>

      {creating && (
        <form onSubmit={submit} className="card mt-4 space-y-3 p-5">
          {formError && <Alert>{formError}</Alert>}
          <Input
            label={t("Class name")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slug) setSlug(slugify(e.target.value));
            }}
            placeholder={t("Bio 101 Study Group")}
          />
          <Input
            label={t("Slug")}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="bio-101"
            hint={t("Lowercase letters, numbers, hyphens. Used in links.")}
          />
          <Input
            label={t("Description (optional)")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="flex items-center gap-3">
            <ClassIcon emoji={emoji} color={color} size={44} />
            <div className="flex flex-wrap gap-1.5">
              {CLASS_EMOJIS.slice(0, 12).map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEmoji(e)}
                  className={cx(
                    "flex h-8 w-8 items-center justify-center rounded-lg text-lg transition",
                    emoji === e ? "bg-brand-100 ring-2 ring-brand-500" : "bg-black/5 hover:bg-black/10",
                  )}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {CLASS_PALETTE.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setColor(key)}
                className={cx("h-6 w-6 rounded-full", classSwatch(key), color === key ? "ring-2 ring-offset-2 ring-ink" : "")}
                title={key}
              />
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={inviteOnly}
              onChange={(e) => setInviteOnly(e.target.checked)}
              className="h-4 w-4 rounded border-black/20 text-brand-600"
            />
            {t("Invite-only (you add members)")}
          </label>
          <Button type="submit" loading={createMut.isPending}>
            {t("Create class")}
          </Button>
        </form>
      )}

      {isLoading && (
        <div className="flex justify-center py-16 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      )}
      {isError && <div className="mt-4"><Alert>{error instanceof Error ? error.message : t("Couldn't load classes.")}</Alert></div>}

      {data && data.length === 0 && !creating && (
        <p className="mt-8 text-center text-ink-faint">{t("You're not in any classes yet. Create one to get started.")}</p>
      )}

      {data && data.length > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {data.map((c) => (
            <Link
              key={c.id}
              to={`/app/community/clans/${c.id}`}
              className="card flex items-center gap-4 p-4 transition hover:-translate-y-0.5 hover:shadow-glow"
            >
              <ClassIcon emoji={c.icon_emoji} color={c.color} size={44} />
              <div className="min-w-0 flex-1">
                <h3 className="truncate font-semibold text-ink">{c.name}</h3>
                <p className="text-sm text-ink-faint">
                  {t("{{count}} members", { count: c.member_count })}
                  {c.my_role && c.my_role !== "member" && ` · ${shortRoleLabel(c.my_role)}`}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
